import { A11yModule } from '@angular/cdk/a11y';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgxEchartsModule } from 'ngx-echarts';
import { CVFocusTrapDirective } from '../../directives/cv-focus-trap/cv-focus-trap.directive';
import { BadgeComponent } from './components/badge/badge.component';
import { ChartBaseComponent } from './components/charts/chart-base/chart-base.component';
import { DeviceSpecificComponent } from './components/device-specific/device-specific.component';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { ExpansionCheckboxPanelComponent } from './components/expansion-checkbox-panel/expansion-checkbox-panel.component';
import { ExpansionPanelComponent } from './components/expansion-panel/expansion-panel.component';
import { FeedbackComponent } from './components/feedback/feedback.component';
import { GalleryComponent } from './components/gallery/gallery.component';
import { IconComponent } from './components/icon/icon.component';
import { InformationBlockComponent } from './components/information-block/information-block.component';
import { InformationDialogComponent } from './components/information-dialog/information-dialog.component';
import { InputBaseComponent } from './components/input-base/input-base.component';
import { InputCheckboxMultistateComponent } from './components/input-checkbox-multistate/input-checkbox-multistate.component';
import { InputCheckboxComponent } from './components/input-checkbox/input-checkbox.component';
import { InputDateComponent } from './components/input-date/input-date.component';
import { InputEditorComponent } from './components/input-editor/input-editor.component';
import { InputMultiselectComponent } from './components/input-multiselect/input-multiselect.component';
import { InputNumberComponent } from './components/input-number/input-number.component';
import { InputRadioComponent } from './components/input-radio/input-radio.component';
import { InputSelectComponent } from './components/input-select/input-select.component';
import { InputSliderComponent } from './components/input-slider/input-slider.component';
import { InputTextComponent } from './components/input-text/input-text.component';
import { InputTextareaComponent } from './components/input-textarea/input-textarea.component';
import { LabelIconComponent } from './components/label-icon/label-icon.component';
import { LazyLoadedImageComponent } from './components/lazy-loaded-image/lazy-loaded-image.component';
import { LoadingSpinnerComponent } from './components/loading-spinner/loading-spinner.component';
import { MiniLoadingSpinnerComponent } from './components/loading-spinner/mini/mini-loading-spinner.component';
import { MenuDropdownComponent } from './components/menu-dropdown/menu-dropdown.component';
import { PanelComponent } from './components/panel/panel.component';
import { RouteLoadIndicatorComponent } from './components/route-load-indicator/route-load-indicator.component';
import { SlideOutTabComponent } from './components/slide-out-tab/slide-out-tab.component';
import { TabListComponent } from './components/tab-list/tab-list.component';
import { BasicCellComponent } from './components/table/components/basic-cell/basic-cell.component';
import { BodyRowComponent } from './components/table/components/body-row/body-row.component';
import { RecursiveRowComponent } from './components/table/components/recursive-row/recursive-row.component';
import { TableComponent } from './components/table/table.component';
import { TargetBarometerComponent } from './components/target-barometer/target-barometer.component';
import { TargetPercentageComponent } from './components/target-percentage/target-percentage.component';
import { ToggleButtonComponent } from './components/toggle-button/toggle-button.component';
import { TooltipHintComponent } from './components/tooltip/tooltip-hint/tooltip-hint.component';
import { TooltipDirective } from './components/tooltip/tooltip.directive';
import { TooltipComponent } from './components/tooltip/tooltip/tooltip.component';
import { TransitionTargetCardComponent } from './components/transition-target-card/transition-target-card.component';
import { CancelMouseHover } from './directives/cancel-mouse-hover.directive';
import { ClickOutsideDirective } from './directives/click-outside.directive';
import { ClickStopPropagation } from './directives/click-stop-propagation.directive';
import { CustomNumberDirective } from './directives/custom-number.directive';
import { CustomScrollDirective } from './directives/custom-scroll.directive';
import { DigitOnlyDirective } from './directives/digit-only.directive';
import { DisableLinkDirective } from './directives/disable-link.directive';
import { FilterDirective } from './directives/filter.directive';
import { MouseDownOutsideDirective } from './directives/mousedown-outside.directive';
import { TableUnsortDirective } from './directives/p-table-unsort.directive';
import { TabOutsideDirective } from './directives/tab-outside.directive';
import { LocaliseNumberDigitsInfoPipe } from './pipes/localise-number-digits-info';
import { PipesModule } from './pipes/pipes.module';
import { PrimeNgModule } from './primeng.module';
import { ButtonComponent } from './ui-kit/buttons/button/button.component';
import { ButtonDirective } from './ui-kit/buttons/button/button.directive';
import { ExternalLinkComponent } from './ui-kit/external-link/external-link.component';
import { ReadOnlyDirective } from './directives/read-only.directive';
import { InputErrorComponent } from './ui-kit/input-error/input-error.component';
import { TooltipDirectiveNew } from './components/tooltip-new/tooltip.directive-new';

const COMPONENTS = [
  ChartBaseComponent,
  TargetPercentageComponent,
  TargetBarometerComponent,
  TransitionTargetCardComponent,
  SlideOutTabComponent,
  FeedbackComponent,
  InformationDialogComponent,
  MenuDropdownComponent,
  GalleryComponent,
  InputTextComponent,
  InputNumberComponent,
  InputSelectComponent,
  InputMultiselectComponent,
  InputRadioComponent,
  InputBaseComponent,
  InputCheckboxComponent,
  InputTextareaComponent,
  DeviceSpecificComponent,
  TooltipHintComponent,
  // TooltipComponent,
  InputDateComponent,
  TableComponent,
  InputCheckboxMultistateComponent,
  InformationBlockComponent,
  InputDateComponent,
  InputEditorComponent,
  ExpansionPanelComponent,
  InputSliderComponent,
  PanelComponent,
  BasicCellComponent,
  BodyRowComponent,
  RecursiveRowComponent,
  BadgeComponent,
  ExpansionCheckboxPanelComponent,
  ToggleButtonComponent,
  LazyLoadedImageComponent,
  DropdownComponent,
  TabListComponent,
];

const STANDALONE_ENTITIES = [
  ButtonComponent,
  ButtonDirective,
  IconComponent,
  LabelIconComponent,
  TooltipDirective,
  TooltipDirectiveNew,
  ExternalLinkComponent,
  CVFocusTrapDirective,
  LoadingSpinnerComponent,
  MiniLoadingSpinnerComponent,
  LocaliseNumberDigitsInfoPipe,
];

const THIRD_PARTY_MODULES = [NgxEchartsModule.forRoot({ echarts: () => import('echarts') })];

const DIRECTIVES = [
  FilterDirective,
  CustomNumberDirective,
  DigitOnlyDirective,
  TableUnsortDirective,
  DisableLinkDirective,
  CustomScrollDirective,
  CancelMouseHover,
  TabOutsideDirective,
  ClickStopPropagation,
  MouseDownOutsideDirective,
  ClickOutsideDirective,
];

@NgModule({
  declarations: [RouteLoadIndicatorComponent, DIRECTIVES, COMPONENTS],
  imports: [
    A11yModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PrimeNgModule,
    PipesModule,
    ScrollingModule,
    THIRD_PARTY_MODULES,
    STANDALONE_ENTITIES,
    ReadOnlyDirective,
    InputErrorComponent,
  ],
  exports: [
    A11yModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PrimeNgModule,
    RouteLoadIndicatorComponent,
    PipesModule,
    ScrollingModule,
    ReadOnlyDirective,
    DIRECTIVES,
    COMPONENTS,
    THIRD_PARTY_MODULES,
    STANDALONE_ENTITIES,
  ],
})
export class SharedModule {}
